'use client';

import { ProvenanceFootnote } from '@/components/ai-review/provenance-footnote';
import { openSourceFile } from '@/components/ai-review/open-source-file';
import type { QuoteSource } from '@/lib/services/ai-analysis/types';
import type { VariantProvenance } from '@/lib/services/identity-variants';

/**
 * Per-row provenance for an identity variant (LF-5566 PROV-21).
 *
 * Every variant row persists its own provenance value-object, so unlike the AI
 * review cards — where one source covers a whole card — this footnote genuinely
 * attests the single value it sits under. It deliberately reuses
 * {@link ProvenanceFootnote} rather than inventing a second vocabulary: the
 * lawyer should read the same quiet "Informed by …" language wherever
 * provenance appears.
 *
 * Renders nothing for a manually-entered variant, whose provenance columns are
 * all null — the absence of a footnote is itself the signal that a person, not
 * the AI, put the value there.
 */

/**
 * Rebuild the wire-shaped {@link QuoteSource} the footnote renders from the flat
 * persisted provenance columns. Returns undefined when the row carries no usable
 * provenance (manual capture, or an AI row whose quote never landed).
 */
function toQuoteSource(provenance: VariantProvenance): QuoteSource | undefined {
  const quote = provenance.quote?.trim();
  if (!quote) return undefined;

  switch (provenance.sourceType) {
    case 'document':
      // A document source without a file id can't offer "View source", but the
      // quote itself is still worth showing.
      return provenance.sourceFileId
        ? {
            type: 'document',
            quote,
            fileId: provenance.sourceFileId,
            ...(provenance.page !== null ? { page: provenance.page } : {})
          }
        : { type: 'transcript', quote };
    case 'transcript':
      return { type: 'transcript', quote };
    case 'form':
      // The originating field id isn't persisted on the variant, so the footnote
      // falls back to the unqualified "the intake form" label.
      return { type: 'form', quote, fieldId: '' };
    default:
      return undefined;
  }
}

interface VariantProvenanceFootnoteProps {
  provenance: VariantProvenance;
  className?: string;
}

export function VariantProvenanceFootnote({
  provenance,
  className
}: VariantProvenanceFootnoteProps) {
  const source = toQuoteSource(provenance);
  if (!source) return null;

  return (
    <ProvenanceFootnote
      source={source}
      // Resolved server-side from the source file, so the footnote can name the
      // document ("the will") instead of the generic fallback.
      sourceFileName={provenance.sourceFileName ?? undefined}
      sourceFileType={provenance.sourceFileType ?? undefined}
      onViewSource={openSourceFile}
      className={className}
    />
  );
}
